import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { Button } from "../components/ui/Button";
import { AnimatedCounter } from "../components/ui/AnimatedCounter";
import { FAQ } from "../components/ui/FAQ";
import { TestimonialCard } from "../components/ui/TestimonialCard";
import { ProgressRing } from "../components/ui/ProgressRing";
import { LiveActivityTicker } from "../components/ui/LiveActivityTicker";
import { OnboardingTutorial } from "../components/OnboardingTutorial";
import { InstallPrompt } from "../components/InstallPrompt";
import { useScrollAnimation } from "../hooks/useScrollAnimation";
import {Droplet, HeartHandshake, Users, Award, MapPin, Clock, TrendingUp, Activity, Heart, Shield, Zap, CheckCircle, ArrowRight, ChevronRight, DollarSign, ExternalLink} from 'lucide-react';
import { lumi } from "../lib/lumi";

export function Home() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [showTutorial, setShowTutorial] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [stats, setStats] = useState({
    activeRequests: 0,
    totalDonors: 0,
    livesSaved: 0
  });
  const [isLoadingStats, setIsLoadingStats] = useState(false);

  const impactSection = useScrollAnimation();
  const howItWorksSection = useScrollAnimation();
  const whyDonateSection = useScrollAnimation();
  const testimonialsSection = useScrollAnimation();
  const faqSection = useScrollAnimation();

  const slides = [
    {
      image: "https://static.lumi.new/material/6a/6a6f9761d8a979031be9161a96330baf.jpeg",
      alt: "Doação de sangue salvando vidas",
      isClickable: false
    },
    {
      image: "https://static.lumi.new/material/3d/3d15d18060db50e6e87e24746349f508.png",
      alt: "2º Lugar no Hackathon Lumi",
      isClickable: true,
      link: "https://lumi.new/pt-BR/hackathon",
      badge: "🥈 2º Lugar no Hackathon Lumi"
    }
  ];

  // Fetch inicial imediato dos dados
  useEffect(() => {
    const fetchStats = async () => {
      try {
        // Buscar apenas totais (mais rápido)
        const [requestsData, profilesData] = await Promise.all([
          lumi.entities.requests.list({ filter: { status: "active" }, limit: 1 }),
          lumi.entities.profiles.list({ filter: { isDonor: true }, limit: 1 })
        ]);

        // Estimativa de vidas salvas (sem buscar todos os perfis)
        const estimatedDonations = profilesData.total * 2.5; // Média estimada

        setStats({
          activeRequests: requestsData.total || 0,
          totalDonors: profilesData.total || 0,
          livesSaved: Math.round(estimatedDonations * 4)
        });
      } catch (error) {
        console.error("Failed to fetch stats:", error);
      }
    };
    
    // Executar imediatamente
    fetchStats();
    
    // Atualizar periodicamente
    const interval = setInterval(fetchStats, 120000); // 2 minutos
    
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const hasSeenTutorial = localStorage.getItem("hasSeenTutorial");
    if (!hasSeenTutorial) {
      setShowTutorial(true);
    }
    
    const refCode = searchParams.get("ref");
    if (refCode) {
      navigate(`/sou-doador?ref=${refCode}`);
    }
  }, []);

  // Auto-slide carousel
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 6000); // 6 segundos para transição mais suave

    return () => clearInterval(interval);
  }, []);

  const handleTutorialComplete = () => {
    localStorage.setItem("hasSeenTutorial", "true");
    setShowTutorial(false);
  };

  const handleTutorialSkip = () => {
    localStorage.setItem("hasSeenTutorial", "true");
    setShowTutorial(false);
  };

  const faqItems = [
    {
      question: "Quem pode doar sangue?",
      answer: "Podem doar sangue pessoas entre 16 e 69 anos (menores de 18 precisam de autorização), com peso acima de 50kg, em bom estado de saúde. É necessário estar alimentado, descansado e apresentar documento oficial com foto."
    },
    {
      question: "Com que frequência posso doar?",
      answer: "Homens podem doar a cada 2 meses (máximo de 4 doações por ano) e mulheres a cada 3 meses (máximo de 3 doações por ano). O intervalo é necessário para a recuperação completa do organismo."
    },
    {
      question: "A doação de sangue dói?",
      answer: "A doação é praticamente indolor. Você sentirá apenas uma pequena picada da agulha no início. O processo todo dura cerca de 40 minutos, sendo apenas 10 minutos a coleta em si."
    },
    {
      question: "Quanto de sangue é doado?",
      answer: "Em cada doação são coletados aproximadamente 450ml de sangue. Essa quantidade é reposta naturalmente pelo organismo em 24-48 horas."
    },
    {
      question: "Uma doação pode salvar quantas vidas?",
      answer: "Uma única doação pode salvar até 4 vidas! Isso porque o sangue é separado em diferentes componentes (hemácias, plaquetas, plasma) que podem beneficiar múltiplos pacientes."
    },
    {
      question: "Como funciona o match de doadores?",
      answer: "Nossa plataforma conecta automaticamente doadores com solicitações próximas geograficamente e compatíveis por tipo sanguíneo. Você receberá notificações quando houver alguém precisando na sua região."
    },
    {
      question: "Meus dados estão seguros?",
      answer: "Sim! Utilizamos criptografia de ponta a ponta e seguimos rigorosamente a LGPD. Seus dados pessoais nunca são compartilhados sem seu consentimento explícito."
    },
    {
      question: "Como confirmo minha doação?",
      answer: "Após doar, você pode confirmar a doação em seu perfil. Isso soma pontos no ranking, desbloqueia badges e ajuda a manter o histórico atualizado para futuras doações."
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {showTutorial && (
        <OnboardingTutorial
          onComplete={handleTutorialComplete}
          onSkip={handleTutorialSkip}
        />
      )}
      <InstallPrompt />
      <Navbar />

      {/* Hero Section - Modern & Clean */}
      <section className="relative pt-20 pb-12 sm:pt-28 sm:pb-16 lg:pt-32 lg:pb-24 overflow-hidden bg-gradient-to-br from-blue-50 via-white to-red-50">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left Content */}
            <div className="text-left">
              <div className="inline-flex items-center space-x-2 bg-red-50 text-[#E53E3E] px-3 py-1.5 sm:px-4 sm:py-2 rounded-full text-xs sm:text-sm font-semibold mb-4 sm:mb-6">
                <Activity className="h-4 w-4" />
                <span>{stats.activeRequests} pessoas precisando agora</span>
              </div>
              
              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold text-[#2D3748] mb-4 sm:mb-6 leading-tight">
                Salve Vidas em <span className="text-[#E53E3E]">Minutos</span>
              </h1>
              
              <p className="text-base sm:text-lg md:text-xl lg:text-2xl text-gray-700 mb-6 sm:mb-8 leading-relaxed">
                Conectamos doadores a quem mais precisa através de tecnologia e solidariedade
              </p>

              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 mb-6 sm:mb-8">
                <Button
                  onClick={() => navigate("/sou-doador")}
                  className="group px-6 py-3 sm:px-8 sm:py-4 text-base sm:text-lg font-bold bg-[#E53E3E] hover:bg-red-600 text-white shadow-xl hover:shadow-2xl transition-all transform hover:-translate-y-1"
                >
                  Quero Doar Agora
                  <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                </Button>
                <Button
                  onClick={() => navigate("/preciso-doar")}
                  variant="outline"
                  className="px-6 py-3 sm:px-8 sm:py-4 text-base sm:text-lg font-bold border-2 border-[#E53E3E] text-[#E53E3E] hover:bg-red-50 transition-all"
                >
                  Preciso de Sangue
                </Button>
              </div>

              {/* Trust Indicators */}
              <div className="flex flex-wrap items-center gap-6 text-sm text-gray-600">
                <div className="flex items-center space-x-2">
                  <CheckCircle className="h-5 w-5 text-green-500" />
                  <span>100% Gratuito</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Shield className="h-5 w-5 text-blue-500" />
                  <span>Dados Seguros (LGPD)</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Zap className="h-5 w-5 text-yellow-500" />
                  <span>Match Instantâneo</span>
                </div>
              </div>
            </div>

            {/* Right Illustration - Carousel */}
            <div className="relative hidden lg:block">
              <div className="relative z-10">
                {/* Carousel Container */}
                <div className="relative overflow-hidden rounded-3xl shadow-2xl h-[450px] lg:h-[500px] xl:h-[550px] bg-gray-100">
                  {slides.map((slide, index) => (
                    <div
                      key={index}
                      className={`absolute inset-0 transition-all duration-1000 ease-in-out ${
                        index === currentSlide
                          ? 'opacity-100 translate-x-0 z-10'
                          : 'opacity-0 translate-x-8 z-0'
                      }`}
                    >
                      {slide.isClickable ? (
                        <a
                          href={slide.link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="block relative group w-full h-full"
                        >
                          <img
                            src={slide.image}
                            alt={slide.alt}
                            loading="eager"
                            className="w-full h-full object-contain object-center rounded-3xl group-hover:scale-[1.02] transition-transform duration-300 bg-white"
                          />
                          {slide.badge && (
                            <div className="absolute top-4 left-4 bg-gradient-to-r from-yellow-400 to-yellow-500 text-gray-900 px-4 py-2 rounded-full font-bold text-sm shadow-lg animate-pulse">
                              {slide.badge}
                            </div>
                          )}
                        </a>
                      ) : (
                        <img
                          src={slide.image}
                          alt={slide.alt}
                          loading="eager"
                          className="w-full h-full object-contain object-center rounded-3xl bg-white"
                        />
                      )}
                    </div>
                  ))}
                </div>

                {/* Slide Indicators */}
                <div className="absolute -bottom-8 left-1/2 transform -translate-x-1/2 flex space-x-2 z-20">
                  {slides.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentSlide(index)}
                      className={`w-2 h-2 rounded-full transition-all duration-300 ${
                        index === currentSlide
                          ? 'bg-[#E53E3E] w-8'
                          : 'bg-gray-300 hover:bg-gray-400'
                      }`}
                      aria-label={`Ir para slide ${index + 1}`}
                    />
                  ))}
                </div>

                <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl shadow-xl p-6 max-w-xs z-10">
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                      <Heart className="h-6 w-6 text-green-600 fill-current" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-gray-900">{stats.livesSaved}+</p>
                      <p className="text-sm text-gray-600">Vidas Salvas</p>
                    </div>
                  </div>
                </div>
              </div>
              {/* Decorative Elements */}
              <div className="absolute top-10 -right-10 w-72 h-72 bg-[#E53E3E] opacity-10 rounded-full blur-3xl"></div>
              <div className="absolute -bottom-10 -left-10 w-64 h-64 bg-blue-500 opacity-10 rounded-full blur-3xl"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Live Activity Ticker */}
      <LiveActivityTicker />

      {/* Impact Stats Section - Sophisticated Design */}
      <section 
        ref={impactSection.elementRef}
        className={`py-20 bg-white transition-all duration-1000 ${
          impactSection.isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
        }`}
      >
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              Nosso Impacto Real
            </h2>
            <p className="text-xl text-gray-700 max-w-3xl mx-auto leading-relaxed">
              Cada número representa uma vida transformada por doadores como você
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {/* Lives Saved Card */}
            <div className="group relative bg-gradient-to-br from-red-50 to-white rounded-3xl p-8 shadow-lg hover:shadow-2xl hover:scale-[1.02] hover:-translate-y-1 transition-all duration-300 border border-red-100">
              <div className="flex justify-between items-start mb-6">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#E53E3E] to-red-600 flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
                  <Heart className="h-8 w-8 text-white fill-current" />
                </div>
                <ProgressRing percentage={85} size={60} strokeWidth={6} color="#E53E3E" />
              </div>
              <div className="text-5xl font-black text-[#E53E3E] mb-2">
                <AnimatedCounter end={stats.livesSaved} duration={2000} />
              </div>
              <p className="text-lg font-semibold text-gray-700 mb-2">Vidas Salvas</p>
              <p className="text-sm text-gray-600">Cada doação salva até 4 vidas</p>
              <div className="mt-4 flex items-center text-green-600 text-sm font-medium">
                <TrendingUp className="h-4 w-4 mr-1" />
                <span>+12% este mês</span>
              </div>
            </div>

            {/* Active Donors Card */}
            <div className="group relative bg-gradient-to-br from-green-50 to-white rounded-3xl p-8 shadow-lg hover:shadow-2xl hover:scale-[1.02] hover:-translate-y-1 transition-all duration-300 border border-green-100">
              <div className="flex justify-between items-start mb-6">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#38A169] to-green-600 flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
                  <Users className="h-8 w-8 text-white" />
                </div>
                <ProgressRing percentage={92} size={60} strokeWidth={6} color="#38A169" />
              </div>
              <div className="text-5xl font-black text-[#38A169] mb-2">
                <AnimatedCounter end={stats.totalDonors} duration={2000} />
              </div>
              <p className="text-lg font-semibold text-gray-700 mb-2">Doadores Ativos</p>
              <p className="text-sm text-gray-600">Prontos para ajudar a qualquer momento</p>
              <div className="mt-4 flex items-center text-green-600 text-sm font-medium">
                <TrendingUp className="h-4 w-4 mr-1" />
                <span>+18% este mês</span>
              </div>
            </div>

            {/* Active Requests Card */}
            <div className="group relative bg-gradient-to-br from-blue-50 to-white rounded-3xl p-8 shadow-lg hover:shadow-2xl hover:scale-[1.02] hover:-translate-y-1 transition-all duration-300 border border-blue-100">
              <div className="flex justify-between items-start mb-6">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#2B6CB0] to-blue-600 flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
                  <Activity className="h-8 w-8 text-white" />
                </div>
                <ProgressRing percentage={78} size={60} strokeWidth={6} color="#2B6CB0" />
              </div>
              <div className="text-5xl font-black text-[#2B6CB0] mb-2">
                <AnimatedCounter end={stats.activeRequests} duration={2000} />
              </div>
              <p className="text-lg font-semibold text-gray-700 mb-2">Pedidos Ativos</p>
              <p className="text-sm text-gray-600">Pessoas precisando de ajuda agora</p>
              <div className="mt-4 flex items-center text-red-600 text-sm font-medium">
                <Clock className="h-4 w-4 mr-1" />
                <span>Urgente</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section - Timeline Design */}
      <section 
        ref={howItWorksSection.elementRef}
        className={`py-20 bg-gradient-to-b from-gray-50 to-white transition-all duration-1000 ${
          howItWorksSection.isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
        }`}
      >
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              Como Funciona
            </h2>
            <p className="text-xl text-gray-700 max-w-3xl mx-auto leading-relaxed">
              Em 3 passos simples, você já faz parte dessa rede solidária
            </p>
          </div>

          <div className="max-w-5xl mx-auto relative">
            {/* Timeline Line - Desktop */}
            <div className="hidden md:block absolute top-32 left-0 right-0 h-1 bg-gradient-to-r from-[#E53E3E] via-[#2B6CB0] to-[#38A169]"></div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {/* Step 1 */}
              <div className="relative group animate-fade-in-delay-1">
                <div className="bg-white rounded-3xl p-8 shadow-lg hover:shadow-2xl hover:scale-[1.02] hover:-translate-y-1 transition-all duration-300 border-2 border-transparent hover:border-[#E53E3E]">
                  <div className="flex justify-center mb-6">
                    <div className="relative">
                      <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-[#E53E3E] to-red-600 flex items-center justify-center text-white text-3xl font-black shadow-xl group-hover:scale-110 transition-transform">
                        1
                      </div>
                      <div className="absolute -top-2 -right-2 w-6 h-6 bg-green-500 rounded-full flex items-center justify-center">
                        <CheckCircle className="h-4 w-4 text-white" />
                      </div>
                    </div>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-3 text-center">
                    Cadastro Rápido
                  </h3>
                  <p className="text-gray-700 text-center leading-relaxed">
                    Informe nome, tipo sanguíneo e localização em menos de 2 minutos
                  </p>
                </div>
              </div>

              {/* Step 2 */}
              <div className="relative group animate-fade-in-delay-2">
                <div className="bg-white rounded-3xl p-8 shadow-lg hover:shadow-2xl hover:scale-[1.02] hover:-translate-y-1 transition-all duration-300 border-2 border-transparent hover:border-[#2B6CB0]">
                  <div className="flex justify-center mb-6">
                    <div className="relative">
                      <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-[#2B6CB0] to-blue-600 flex items-center justify-center text-white text-3xl font-black shadow-xl group-hover:scale-110 transition-transform">
                        2
                      </div>
                      <div className="absolute -top-2 -right-2 w-6 h-6 bg-yellow-500 rounded-full flex items-center justify-center">
                        <Zap className="h-4 w-4 text-white" />
                      </div>
                    </div>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-3 text-center">
                    Match Automático
                  </h3>
                  <p className="text-gray-700 text-center leading-relaxed">
                    Conectamos você com solicitações compatíveis próximas a você
                  </p>
                </div>
              </div>

              {/* Step 3 */}
              <div className="relative group animate-fade-in-delay-3">
                <div className="bg-white rounded-3xl p-8 shadow-lg hover:shadow-2xl hover:scale-[1.02] hover:-translate-y-1 transition-all duration-300 border-2 border-transparent hover:border-[#38A169]">
                  <div className="flex justify-center mb-6">
                    <div className="relative">
                      <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-[#38A169] to-green-600 flex items-center justify-center text-white text-3xl font-black shadow-xl group-hover:scale-110 transition-transform">
                        3
                      </div>
                      <div className="absolute -top-2 -right-2 w-6 h-6 bg-[#E53E3E] rounded-full flex items-center justify-center">
                        <Heart className="h-4 w-4 text-white fill-current" />
                      </div>
                    </div>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-3 text-center">
                    Salve Vidas
                  </h3>
                  <p className="text-gray-700 text-center leading-relaxed">
                    Confirme sua doação e acompanhe seu impacto no ranking
                  </p>
                </div>
              </div>
            </div>

            <div className="text-center mt-12">
              <button
                onClick={() => setShowTutorial(true)}
                className="inline-flex items-center text-[#2B6CB0] hover:text-blue-700 font-semibold text-lg transition-colors group"
              >
                Ver tutorial interativo completo
                <ChevronRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Why Donate Section */}
      <section 
        ref={whyDonateSection.elementRef}
        className={`py-20 bg-[#F8F9FA] transition-all duration-1000 ${
          whyDonateSection.isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
        }`}
      >
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              Por Que Doar?
            </h2>
            <p className="text-xl text-gray-700 max-w-3xl mx-auto leading-relaxed">
              Benefícios que vão além de salvar vidas
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-6xl mx-auto">
            {/* Benefit 1 */}
            <div className="bg-white rounded-2xl p-8 text-center shadow-md hover:shadow-xl hover:scale-[1.02] hover:-translate-y-1 transition-all duration-300 border border-gray-100">
              <div className="w-16 h-16 rounded-2xl bg-red-100 flex items-center justify-center mx-auto mb-6">
                <Heart className="h-8 w-8 text-[#E53E3E]" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Saúde do Doador</h3>
              <p className="text-gray-700 text-sm leading-relaxed">
                Check-up gratuito a cada doação, detectando possíveis problemas de saúde
              </p>
            </div>

            {/* Benefit 2 */}
            <div className="bg-white rounded-2xl p-8 text-center shadow-md hover:shadow-xl hover:scale-[1.02] hover:-translate-y-1 transition-all duration-300 border border-gray-100">
              <div className="w-16 h-16 rounded-2xl bg-blue-100 flex items-center justify-center mx-auto mb-6">
                <Users className="h-8 w-8 text-[#2B6CB0]" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Impacto Social</h3>
              <p className="text-gray-700 text-sm leading-relaxed">
                Uma doação pode salvar até 4 vidas, impactando famílias inteiras
              </p>
            </div>

            {/* Benefit 3 */}
            <div className="bg-white rounded-2xl p-8 text-center shadow-md hover:shadow-xl hover:scale-[1.02] hover:-translate-y-1 transition-all duration-300 border border-gray-100">
              <div className="w-16 h-16 rounded-2xl bg-yellow-100 flex items-center justify-center mx-auto mb-6">
                <Award className="h-8 w-8 text-yellow-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Reconhecimento</h3>
              <p className="text-gray-700 text-sm leading-relaxed">
                Ganhe pontos, badges e destaque no ranking regional de heróis
              </p>
            </div>

            {/* Benefit 4 */}
            <div className="bg-white rounded-2xl p-8 text-center shadow-md hover:shadow-xl hover:scale-[1.02] hover:-translate-y-1 transition-all duration-300 border border-gray-100">
              <div className="w-16 h-16 rounded-2xl bg-green-100 flex items-center justify-center mx-auto mb-6">
                <HeartHandshake className="h-8 w-8 text-[#38A169]" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Comunidade</h3>
              <p className="text-gray-700 text-sm leading-relaxed">
                Faça parte de uma rede solidária de milhares de doadores
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section 
        ref={testimonialsSection.elementRef}
        className={`py-20 bg-white transition-all duration-1000 ${
          testimonialsSection.isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
        }`}
      >
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              Histórias Reais
            </h2>
            <p className="text-xl text-gray-700 max-w-3xl mx-auto leading-relaxed">
              Veja o que doadores e receptores têm a dizer
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            <TestimonialCard
              name="Maria Silva"
              city="São Paulo, SP"
              bloodType="O+"
              testimonial="Doei pela primeira vez através da plataforma e foi incrível ver o impacto direto. Recebi uma mensagem da família agradecendo. Emocionante!"
              rating={5}
            />
            <TestimonialCard
              name="João Santos"
              city="Rio de Janeiro, RJ"
              bloodType="A+"
              testimonial="A plataforma me conectou com um paciente a 5km de casa. Em menos de 2 horas estava doando. Tecnologia salvando vidas de verdade!"
              rating={5}
            />
            <TestimonialCard
              name="Ana Costa"
              city="Belo Horizonte, MG"
              bloodType="B-"
              testimonial="Como receptora, agradeço imensamente aos doadores. Minha filha recebeu sangue em uma cirurgia crítica. Vocês são verdadeiros heróis."
              rating={5}
            />
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section 
        ref={faqSection.elementRef}
        className={`py-20 bg-gradient-to-b from-gray-50 to-white transition-all duration-1000 ${
          faqSection.isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
        }`}
      >
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              Perguntas Frequentes
            </h2>
            <p className="text-xl text-gray-700 max-w-3xl mx-auto leading-relaxed">
              Tire suas dúvidas sobre doação de sangue
            </p>
          </div>

          <div className="max-w-4xl mx-auto">
            <FAQ items={faqItems} />
          </div>

          <div className="text-center mt-12">
            <p className="text-gray-700 mb-4">Ainda tem dúvidas?</p>
            <Button
              onClick={() => navigate("/blog")}
              variant="outline"
              className="border-2 border-[#2B6CB0] text-[#2B6CB0] hover:bg-blue-50"
            >
              Visite nosso Blog Educativo
            </Button>
          </div>
        </div>
      </section>

      {/* Partners Section - Elegant & Subtle */}
      <section className="py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              Parceiros que Apoiam o Projeto
            </h2>
            <p className="text-xl text-gray-700 max-w-3xl mx-auto leading-relaxed">
              Plataformas que acreditam no impacto social da tecnologia
            </p>
          </div>

          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-2 md:grid-cols-5 gap-6">
              {/* Buy Me a Coffee */}
              <a
                href="https://buymeacoffee.com/conectavidas"
                target="_blank"
                rel="noopener noreferrer"
                className="group bg-white rounded-2xl p-8 shadow-md hover:shadow-xl transition-all duration-300 hover:scale-[1.05] hover:-translate-y-1 border-2 border-transparent hover:border-yellow-200 flex flex-col items-center justify-center"
              >
                <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-yellow-100 to-yellow-50 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                  <Heart className="h-8 w-8 text-yellow-600 fill-current" />
                </div>
                <p className="text-gray-800 font-bold text-center group-hover:text-yellow-600 transition-colors text-sm">
                  Buy Me a Coffee
                </p>
                <p className="text-xs text-gray-500 mt-1 text-center">Apoio global</p>
              </a>

              {/* Mercado Pago */}
              <a
                href="https://link.mercadopago.com.br/apoieocriador"
                target="_blank"
                rel="noopener noreferrer"
                className="group bg-white rounded-2xl p-8 shadow-md hover:shadow-xl transition-all duration-300 hover:scale-[1.05] hover:-translate-y-1 border-2 border-transparent hover:border-blue-200 flex flex-col items-center justify-center"
              >
                <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-blue-100 to-blue-50 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                  <DollarSign className="h-8 w-8 text-blue-600" />
                </div>
                <p className="text-gray-800 font-bold text-center group-hover:text-blue-600 transition-colors">
                  Mercado Pago
                </p>
                <p className="text-xs text-gray-500 mt-1 text-center">Parceiro Brasil</p>
              </a>

              {/* Benfeitoria */}
              <a
                href="https://benfeitoria.com/projeto/conectandodoadores"
                target="_blank"
                rel="noopener noreferrer"
                className="group bg-white rounded-2xl p-8 shadow-md hover:shadow-xl transition-all duration-300 hover:scale-[1.05] hover:-translate-y-1 border-2 border-transparent hover:border-green-200 flex flex-col items-center justify-center"
              >
                <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-green-100 to-green-50 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                  <Heart className="h-8 w-8 text-green-600" />
                </div>
                <p className="text-gray-800 font-bold text-center group-hover:text-green-600 transition-colors">
                  Benfeitoria
                </p>
                <p className="text-xs text-gray-500 mt-1 text-center">Crowdfunding</p>
              </a>

              {/* Apoia.se */}
              <a
                href="https://apoia.se/conectavidas"
                target="_blank"
                rel="noopener noreferrer"
                className="group bg-white rounded-2xl p-8 shadow-md hover:shadow-xl transition-all duration-300 hover:scale-[1.05] hover:-translate-y-1 border-2 border-transparent hover:border-purple-200 flex flex-col items-center justify-center"
              >
                <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-purple-100 to-purple-50 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                  <Shield className="h-8 w-8 text-purple-600" />
                </div>
                <p className="text-gray-800 font-bold text-center group-hover:text-purple-600 transition-colors">
                  Apoia.se
                </p>
                <p className="text-xs text-gray-500 mt-1 text-center">Financiamento</p>
              </a>

              {/* Kickante */}
              <a
                href="https://www.kickante.com.br/crowdfunding/doe-suporte-para-doacao-de-sangue"
                target="_blank"
                rel="noopener noreferrer"
                className="group bg-white rounded-2xl p-8 shadow-md hover:shadow-xl transition-all duration-300 hover:scale-[1.05] hover:-translate-y-1 border-2 border-transparent hover:border-red-200 flex flex-col items-center justify-center"
              >
                <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-red-100 to-red-50 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                  <Users className="h-8 w-8 text-red-600" />
                </div>
                <p className="text-gray-800 font-bold text-center group-hover:text-red-600 transition-colors">
                  Kickante
                </p>
                <p className="text-xs text-gray-500 mt-1 text-center">Vaquinha online</p>
              </a>
            </div>

            {/* Subtle Support Message */}
            <div className="text-center mt-12">
              <p className="text-gray-600 text-sm max-w-2xl mx-auto leading-relaxed">
                Plataforma 100% sem fins lucrativos. Parceiros globais que acreditam na tecnologia como ferramenta de transformação social.
              </p>
              <p className="text-xs text-gray-500 mt-2">
                🌍 Conectando doadores em todo o mundo • 💝 Cada contribuição salva vidas
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-20 bg-gradient-to-br from-[#E53E3E] to-red-700 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 left-10 w-64 h-64 bg-white rounded-full blur-3xl"></div>
          <div className="absolute bottom-10 right-10 w-96 h-96 bg-white rounded-full blur-3xl"></div>
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center text-white">
            <div className="w-20 h-20 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center mx-auto mb-8">
              <Droplet className="h-10 w-10 text-white fill-current" />
            </div>
            
            <h2 className="text-4xl lg:text-5xl xl:text-6xl font-bold mb-6">
              Comece a Salvar Vidas Hoje
            </h2>
            
            <p className="text-xl lg:text-2xl mb-12 text-red-50 leading-relaxed">
              Cada doação pode salvar até 4 vidas. Junte-se a milhares de heróis que já fazem a diferença.
            </p>

            <div className="flex flex-col sm:flex-row gap-6 justify-center">
              <Button
                onClick={() => navigate("/sou-doador")}
                className="group px-10 py-5 text-lg font-bold bg-white text-[#E53E3E] hover:bg-gray-100 shadow-2xl hover:shadow-3xl transition-all transform hover:-translate-y-1"
              >
                Cadastrar como Doador
                <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
              </Button>
              <Button
                onClick={() => navigate("/solicitacoes")}
                variant="outline"
                className="group px-10 py-5 text-lg font-bold border-2 border-white text-white hover:bg-white/10 transition-all"
              >
                Ver Quem Precisa
                <MapPin className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
              </Button>
            </div>

            <p className="mt-12 text-red-100 text-sm">
              Grátis • Seguro • Impacto Imediato
            </p>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
